import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { typeLabel } from '../shared/fieldTypes';
import { BLOCKS, INPUTS, SINGLE_CONTROL_TYPES } from './inputs';
import { phone } from './palette';

// One block of the in-app fill: a question (label, control, then the error
// in red or the help text) or a content block. The wrapper takes the ref the
// form uses to scroll to it.

const Wrap = styled.div`
  margin-bottom: 18px;
`;

// Design: .phone .m-label (15px bold on the phone body's 1.5 line height).
const Label = styled.div`
  display: block;
  margin-bottom: 9px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.1px;
  color: ${(p) => phone(p.theme).text};
  overflow-wrap: anywhere;
`;

const Req = styled.span`
  color: ${(p) => phone(p.theme).error};
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
`;

const Note = styled.div`
  margin-top: 5px;
  font-size: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${(p) => (p.$error ? phone(p.theme).error : phone(p.theme).text3)};
  font-weight: ${(p) => (p.$error ? 600 : 400)};
`;

const PreviewField = forwardRef(
  ({ field, value, onChange, error, assetUrls }, ref) => {
    const Block = BLOCKS[field.type];
    if (Block) {
      return (
        <Wrap ref={ref}>
          <Block field={field} assetUrls={assetUrls} />
        </Wrap>
      );
    }
    const Control = INPUTS[field.type];
    if (!Control) return null;

    const base = `pv-${field.id}`;
    const inputId = `${base}-input`;
    const labelId = `${base}-label`;
    const noteId = `${base}-note`;
    const help = typeof field.help === 'string' ? field.help.trim() : '';
    const single = SINGLE_CONTROL_TYPES.includes(field.type);
    const label =
      (typeof field.label === 'string' && field.label.trim()) ||
      typeLabel(field.type);

    return (
      <Wrap ref={ref} data-field-id={field.id}>
        <Label
          as={single ? 'label' : 'div'}
          id={labelId}
          htmlFor={single ? inputId : undefined}
        >
          {label}
          {field.required ? <Req aria-hidden="true"> *</Req> : null}
        </Label>
        <Control
          field={field}
          value={value}
          onChange={onChange}
          inputId={inputId}
          labelId={labelId}
          describedBy={error || help ? noteId : undefined}
          invalid={!!error}
        />
        {error || help ? (
          <Note id={noteId} $error={!!error}>
            {error || help}
          </Note>
        ) : null}
      </Wrap>
    );
  }
);

PreviewField.displayName = 'PreviewField';

PreviewField.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  assetUrls: PropTypes.object.isRequired,
};

PreviewField.defaultProps = {
  value: undefined,
  error: undefined,
};

export default PreviewField;
